import axios, { AxiosError } from 'axios';
import { useMemo } from 'react';

export interface FileUploadApi {
  /**
   * Uploads the given file to the remote server.
   *
   * @param file The file to be uploaded
   * @returns The unique id of the file that was uploaded
   * @throws An error whose body details the reason the upload failed. The body of the error will be shown as the
   * error message in the `FileUpload` control.
   */
  uploadFile: (file: File) => Promise<string>;

  /**
   * Removes the file from the remote server.
   *
   * @param fileId Identifies the file to be deleted
   * @throws An error whose body details the reason the remove failed. The body of the error will be shown as the
   * error message in the `FileUpload` control.
   */
  removeFile: (fileId: string) => Promise<void>;
}

const getErrorMessage = (error: AxiosError<{ message: string }>) => {
  return error?.response?.status === 403 ? error.response.data.message : 'The request was unsuccessful.';
};

const uploadFile =
  (endpoint: string) =>
  async (file: File): Promise<string> => {
    const content = new FormData();
    content.append('file', file);

    try {
      const response = await axios({ method: 'POST', url: `${endpoint}/${file.name}`, data: content });
      if (response.status >= 200 && response.status < 300) {
        // set the newly uploaded file so we can return it (as a promise)
        return response.data as string;
      }
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  };

const removeFile =
  (endpoint: string) =>
  async (fileId: string): Promise<void> => {
    let response;

    try {
      response = await axios({
        method: 'delete',
        url: `${endpoint}/${fileId}`,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }

    if (response.status < 200 || response.status >= 300) {
      throw new Error('The file could not be removed');
    }
  };

export const createFileUploadApi = (endpoint: string): FileUploadApi => ({
  uploadFile: uploadFile(endpoint),
  removeFile: removeFile(endpoint),
});

export const useFileUploadApi = (endpoint: string): FileUploadApi => {
  return useMemo(() => createFileUploadApi(endpoint), [endpoint]);
};
